import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';

const resetPasswordSchema = z.object({
  password: z.string().min(6, 'A senha deve ter pelo menos 6 caracteres'),
  confirmPassword: z.string().min(6, 'A senha deve ter pelo menos 6 caracteres'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    // Verificar se há um token de recuperação válido na URL
    const checkRecoveryToken = async () => {
      try {
        // Primeiro, verificar se há hash params (que o Supabase envia)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        
        // Se é type=recovery, redirecionar para a edição do próprio usuário.
        // /usuarios trata ?editar=self e redireciona para /gestao-usuario.
        if (type === 'recovery') {
          console.log('Recovery token detectado, redirecionando para edição de senha...');
          // Aguardar um momento para o Supabase processar a sessão
          await new Promise(resolve => setTimeout(resolve, 500));
          navigate('/usuarios?editar=self&senha=true&recovery=true', { replace: true });
          return;
        }

        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Erro ao verificar sessão:', error);
          toast({
            variant: "destructive",
            title: "Link inválido ou expirado",
            description: "Por favor, solicite um novo link de recuperação de senha.",
          });
          setTimeout(() => navigate('/forgot-password'), 3000);
          return;
        }

        if (session) {
          setIsValidToken(true);
        } else {
          toast({
            variant: "destructive",
            title: "Link inválido ou expirado",
            description: "Por favor, solicite um novo link de recuperação de senha.",
          });
          setTimeout(() => navigate('/forgot-password'), 3000);
        }
      } catch (error) {
        console.error('Erro ao verificar token:', error);
        toast({
          variant: "destructive",
          title: "Erro ao validar link",
          description: "Por favor, tente novamente.",
        });
        setTimeout(() => navigate('/forgot-password'), 3000);
      } finally {
        setIsCheckingToken(false);
      }
    };

    checkRecoveryToken();
  }, [navigate, toast]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Erro ao redefinir senha",
          description: error.message || "Não foi possível redefinir sua senha.",
        });
        return;
      }

      setPasswordChanged(true);
      toast({
        title: "Senha alterada com sucesso!",
        description: "Você será redirecionado para o login.",
      });

      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro inesperado",
        description: "Ocorreu um erro. Tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Mostrar loading enquanto verifica o token
  if (isCheckingToken) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 "
        style={{ backgroundColor: '#F1EDDF' }}
      >
        <Card className="w-full max-w-md shadow-2xl backdrop-blur-sm bg-white/90 border border-white/20">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" 
                   style={{ borderTopColor: '#10261F' }} />
              <p className="text-gray-600">Verificando link de recuperação...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Se o token não for válido, não mostrar o formulário
  if (!isValidToken) {
    return null;
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 "
      style={{ backgroundColor: '#F1EDDF' }}
    >
      <Card className="w-full max-w-md shadow-2xl backdrop-blur-sm bg-white/90 border border-white/20">
        <CardHeader className="text-center space-y-6 pb-8">
          <div>
            <h1 
              className="text-3xl font-bold tracking-tight mb-2"
              style={{ color: '#10261F' }}
            >
              musa
            </h1>
          </div>
          <div>
            <CardTitle 
              className="text-2xl font-semibold"
              style={{ color: '#10261F' }}
            >
              {passwordChanged ? 'Senha alterada!' : 'Redefinir senha'}
            </CardTitle>
            <CardDescription className="text-gray-600 mt-2">
              {passwordChanged 
                ? 'Sua senha foi alterada com sucesso.'
                : 'Digite sua nova senha'
              }
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {!passwordChanged ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Nova senha
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Digite sua nova senha"
                            disabled={isLoading}
                            className="h-12 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all duration-200 pr-12"
                            style={{ 
                              focusRing: '#10261F',
                              '--tw-ring-color': '#10261F'
                            } as React.CSSProperties}
                            aria-label="Nova senha"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isLoading}
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-500" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Confirmar nova senha
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Digite novamente sua nova senha"
                            disabled={isLoading}
                            className="h-12 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all duration-200 pr-12"
                            style={{ 
                              focusRing: '#10261F',
                              '--tw-ring-color': '#10261F'
                            } as React.CSSProperties}
                            aria-label="Confirmar nova senha"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={isLoading}
                            aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-500" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full h-12 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90 focus:ring-2 focus:ring-offset-2"
                  style={{ 
                    backgroundColor: '#10261F',
                    '--tw-ring-color': '#10261F'
                  } as React.CSSProperties}
                  disabled={isLoading}
                  aria-label="Redefinir senha"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Alterando...</span>
                    </div>
                  ) : (
                    'Redefinir senha'
                  )}
                </Button>
              </form>
            </Form>
          ) : (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                     style={{ backgroundColor: '#10261F1A' }}>
                  <CheckCircle className="w-8 h-8" style={{ color: '#10261F' }} />
                </div>
                <p className="text-sm text-gray-600">
                  Redirecionando para o login...
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
